import {Injectable} from '@angular/core';
import {AlertController, type AlertInput} from '@ionic/angular';
import {HttpClient, HttpStatusCode} from '@angular/common/http';
import {BehaviorSubject, delay, firstValueFrom, type Observable} from 'rxjs';
import {filter, first, map, switchMap} from 'rxjs/operators';
import {StorageService} from './storage.service';
import type {AuthUserInterface} from 'src/app/interfaces';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  signInUrl: string;
  private readonly loadingUser$ = new BehaviorSubject<null | 'loading' | 'loaded' | 'failed'>(null);
  readonly isOnline$ = new BehaviorSubject(false); // gets set from app.service onNetworkChange Observable
  readonly isSignedIn$ = new BehaviorSubject(false);
  readonly isSignedOut$ = new BehaviorSubject(false);
  readonly isActive$ = new BehaviorSubject(false);
  // copy user from storage
  readonly user$ = this.storage.user$;

  constructor(
    private readonly alert: AlertController,
    private readonly http: HttpClient,
    private readonly storage: StorageService,
  ) {
    // reset on completion so it can be called again
    this.loadingUser$.pipe(
      filter((state) => !!state && state !== 'loading'),
      delay(5000),
    ).subscribe(() => {
      console.log('user load completed, resetting...');
      this.loadingUser$.next(null);
    });

    this.user$.subscribe((user) => {
      this.isSignedIn$.next(!!user);
      this.isActive$.next(!!user && (!user.disabledAt || new Date(user.disabledAt).getTime() <= new Date().getTime()));
    });

    this.isSignedIn$.subscribe((signedIn) => {
      this.isSignedOut$.next(!signedIn);
    });

    this.isOnline$.pipe(
      filter((online) => online),
      first(),
      switchMap(() => this.http.get('/api/auth/sign-in-url', {observe: 'response', responseType: 'text'})),
    ).subscribe((response) => {
      this.signInUrl = response.ok ? response.body : undefined;
      this.loadUser().then();
    });
  }

  async loadUser(): Promise<boolean> {
    if (this.loadingUser$.value === null) {
      console.log('Loading user...');
      this.loadingUser$.next('loading');
      try {
        const response = await firstValueFrom(
          this.http.get<AuthUserInterface>('/api/auth/user', {
            withCredentials: true,
            observe: 'response',
            responseType: 'json',
          }),
        );

        if (response.ok) {
          const user = response.body;
          await this.storage.setUser(user);
        }

        this.loadingUser$.next(response.ok ? 'loaded' : 'failed');
        return response.ok;
      } catch (error) {
        this.loadingUser$.next('failed');
        if (error.status === HttpStatusCode.NotFound) {
          if (!!this.user$.value) {
            await this.storage.removeUser();
            window.location.replace('/');
          }
          return false;
        } else {
          throw error;
        }
      }
    } else {
      console.log('user already called, awaiting result...');
      return await firstValueFrom(this.loadingUser$.pipe(
        filter((state) => !!state && state !== 'loading'),
        map((state) => state === 'loaded'),
      ));
    }
  }

  get resno(): string {
    const user = this.user$.value;
    return user?.resno ?? '';
  }

  get subjectId(): string {
    const user = this.user$.value;
    return user?.subjectId ?? '';
  }

  async refreshCsrfCookie(): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.http.get<void>('/api/sanctum/csrf-cookie', {observe: 'response'}),
      );

      return response.ok;
    } catch (error) {
      console.log('refresh csrf cookie error: ', error);
      return false;
    }
  }

  signIn(prompt = false): void {
    if (this.isOnline$.value) {
      const url = this.signInUrl;
      const query = this.storage.isKioskMode$.value ? ['kiosk=true'] : [];
      if (prompt) {
        query.push('prompt=true');
      }
      window.location.href = query.length > 0 && !!url ? `${url}?${query.join('&')}` : url ?? '/';
    } else {
      this.alert
        .create({
          header: 'Sign In',
          message: 'You must be online to sign in. Please check your connection and try again.',
          buttons: [
            {
              text: 'Okay',
              handler: () => {
              },
            },
          ],
        })
        .then((alert) => alert.present());
    }
  }

  signOut(): void {
    const inputs: AlertInput[] = this.storage.isAppMode$.value ? [
      {
        type: 'checkbox',
        label: 'Full Sign Out (all apps)',
        value: 'full',// value if checked
        checked: false,// default
      },
    ] : [];

    this.alert
      .create({
        header: 'Sign Out',
        message: 'You can be online or offline to sign out, but you must be online to sign back in. Are you sure you want to sign out?',
        inputs,
        buttons: [
          {
            text: 'Cancel',
            handler: () => {
            },
          },
          {
            text: 'Confirm',
            handler: (inputs?: any[]) => {
              this.storage.removeUser().then(() => {
                if (this.isOnline$.value) {
                  this.signOutOfAuth(inputs?.length > 0 && inputs[0] === 'full');
                } else {
                  window.location.replace('/');
                }
              });
            },
          },
        ],
      })
      .then((alert) => alert.present());
  }

  // full used for optionally signing out of azure
  signOutOfAuth(full = false): void {
    window.location.href = `/api/auth/sign-out${(full ? '?full=true' : '')}`;
  }
}

export interface PersonalInfo {
  givenName: string;
  familyName: string;
  resno: string;
}

export interface AuthInterface {
  isAuthenticated$: Observable<boolean>;
  user$: Observable<AuthUserInterface>;

  signIn(): Promise<void>;

  signOut(): Promise<void>;
}
